import axios, { AxiosInstance } from 'axios'
import { getCsrfToken } from './utils'

/**
 * Dynamically determine the API base URL based on the current domain.
 * This allows a single build to work across multiple domains:
 * - app.devfctrz.com -> api.devfctrz.com
 * - app.chatterpillar.io -> api.chatterpillar.io
 * - localhost development -> relative /api path
 */
function getApiBaseUrl(): string {
  // Allow override via environment variable (useful for development)
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL
  }

  const hostname = window.location.hostname
  const protocol = window.location.protocol

  // Production: app.devfctrz.com -> api.devfctrz.com
  if (hostname === 'app.devfctrz.com' || hostname.endsWith('.devfctrz.com')) {
    return `${protocol}//api.devfctrz.com`
  }

  // Production: app.chatterpillar.io -> api.chatterpillar.io
  if (hostname === 'app.chatterpillar.io' || hostname.endsWith('.chatterpillar.io')) {
    return `${protocol}//api.chatterpillar.io`
  }

  // Development: use relative path (Vite proxy handles it)
  return '/api'
}

const API_BASE_URL = getApiBaseUrl()

// Debug: Log the API URL being used
console.log('[API Config] Hostname:', window.location.hostname)
console.log('[API Config] Using baseURL:', API_BASE_URL)

// Create axios instance with default config
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true, // Important for Django session/CSRF
})

// Extract tenant subdomain from stored auth data (localStorage)
// Fallback to URL subdomain for backward compatibility
function getTenantSubdomain(): string | null {
  // Priority 1: Get from localStorage (set during login)
  try {
    const stored = localStorage.getItem('tenant')
    if (stored) {
      const tenant = JSON.parse(stored)
      if (tenant.subdomain) {
        return tenant.subdomain
      }
    }
  } catch (e) {
    console.error('[API] Error reading tenant from localStorage:', e)
  }
  
  // Priority 2: Fallback to URL subdomain (backward compatibility)
  const hostname = window.location.hostname
  
  // Skip if we're on the static app domain
  if (hostname === 'app.devfctrz.com' || hostname === 'app.chatterpillar.io' || hostname === 'app.localhost') {
    return null
  }
  
  // Extract subdomain from hostname
  // e.g., fctrz.devfctrz.com -> fctrz
  // e.g., fctrz.chatterpillar.io -> fctrz
  // e.g., fctrz.localhost -> fctrz
  if (hostname.includes('.devfctrz.com')) {
    const parts = hostname.split('.devfctrz.com')[0]
    return parts.includes('.') ? null : parts
  }
  if (hostname.includes('.chatterpillar.io')) {
    const parts = hostname.split('.chatterpillar.io')[0]
    return parts.includes('.') ? null : parts
  }
  if (hostname.includes('.localhost')) {
    const parts = hostname.split('.localhost')[0]
    return parts.includes('.') ? null : parts
  }
  
  return null
}

// Add CSRF token and tenant subdomain to all requests
api.interceptors.request.use((config) => {
  const csrfToken = getCsrfToken()
  if (csrfToken) {
    config.headers['X-CSRFToken'] = csrfToken
  }
  
  // Add tenant subdomain header for cross-domain API calls
  const tenantSubdomain = getTenantSubdomain()
  if (tenantSubdomain) {
    config.headers['X-Tenant-Subdomain'] = tenantSubdomain
  }
  
  // For FormData uploads, remove Content-Type so axios can set multipart/form-data with boundary
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type']
  }
  
  return config
})

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Redirect to login
      window.location.href = '/accounts/login/'
    }
    return Promise.reject(error)
  }
)

export default api
